/**
 * Dentis Cart Drawer — Eevy campaign-aware repaint.
 *
 * The cart drawer is rendered server-side from Liquid, which can only read the
 * BASE `shop.metafields.eevy.bundle_config` (Liquid has no shopper campaign
 * context). So the bundle parent line + the config-driven child economics in
 * the drawer show BASE bundle prices even when an Eevy bundle CAMPAIGN is the
 * shopper's actually-live offer. The PDP/picker already solve this by consuming
 * the EFFECTIVE (campaign-aware) config the sovereign Eevy runtime resolves;
 * this module brings the SAME single source of truth to the cart surface.
 *
 * Source of truth (NOT a second resolver): `window.EevyBundleResolved`, the
 * resolution published by dentis-bundle-config.liquid via window.Eevy.bundles
 * ({ base, effective, campaignKey, campaign }). When that hasn't published yet
 * we re-resolve through the very same runtime API as a fallback.
 *
 * What it repaints (display only — checkout money stays owned by the applied
 * campaign code server-side):
 *   - the bundle PARENT line price -> the effective campaign tier offer
 *   - the config-driven CHILD economics -> the effective tier components,
 *     including per-component `final_price_cents` when the Eevy resolver
 *     publishes exact campaign child offers
 *
 * Persistence: re-applies on `eevy:bundle-config-resolved` (campaign change)
 * AND `dentis:cart-drawer:updated` (cart-sync re-renders the drawer innerHTML
 * from fresh BASE Liquid). Because it RE-DERIVES from the resolver and re-paints
 * the freshly rendered DOM every time — never caching a single value that gets
 * clobbered — the campaign economics survive the re-render cycle.
 *
 * No-op safety: with no active campaign, or when the effective offer equals the
 * base offer, it touches nothing — the non-campaign cart keeps its exact Liquid/
 * cart-truth display. GRATIS (free component) lines are never converted to a
 * price, preserving the free-gift rendering.
 */
(function () {
  'use strict';

  var DBG = ((typeof window !== 'undefined' && window.DentisDebug)
    || { enabled: false, log: function () {}, warn: function () {}, error: function () {} });

  function toInt(v) { var n = parseInt(v, 10); return isNaN(n) ? 0 : n; }

  function withThousands(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  // Shopify money-format placeholders -> [thousands, decimal, keepDecimals].
  var MONEY_PLACEHOLDERS = {
    amount: [',', '.', true],
    amount_no_decimals: [',', '.', false],
    amount_with_comma_separator: ['.', ',', true],
    amount_no_decimals_with_comma_separator: ['.', ',', false],
    amount_with_space_separator: [' ', ',', true],
    amount_no_decimals_with_space_separator: [' ', ',', false],
    amount_with_apostrophe_separator: ["'", '.', true],
    amount_with_period_and_space_separator: [' ', '.', true]
  };

  // formatWithShopFormat: Liquid's `money_without_trailing_zeros` against the
  // shop's money_format (emitted by the drawer Liquid as
  // data-dentis-money-format), so a repainted price reads exactly like its
  // Liquid-rendered neighbours — e.g. DKK "489,50 kr" / "1.018 kr", where the
  // sample-sniffing fallback below would round to "490 kr" / "1 018 kr".
  function formatWithShopFormat(cents, format) {
    var text = String(format).replace(/<[^>]*>/g, '');
    var m = text.match(/\{\{\s*(\w+)\s*\}\}/);
    var spec = m && MONEY_PLACEHOLDERS[m[1]];
    if (!spec) return null;
    var negative = cents < 0;
    var abs = Math.round(Math.abs(Number(cents)));
    var whole = Math.floor(abs / 100);
    var fraction = abs % 100;
    var amount;
    if (spec[2] && fraction !== 0) {
      amount = withThousands(String(whole), spec[0]) + spec[1] + (fraction < 10 ? '0' : '') + fraction;
    } else {
      amount = withThousands(String(spec[2] ? whole : Math.round(abs / 100)), spec[0]);
    }
    return text.replace(m[0], (negative ? '-' : '') + amount).trim();
  }

  function moneyFormatOf(el) {
    var f = el && el.getAttribute ? el.getAttribute('data-dentis-money-format') : null;
    return f && f.indexOf('{{') > -1 ? f : null;
  }

  // formatMoney: the shop format when known; otherwise integer kr with the
  // unit/separators sniffed from a sample string already in the DOM.
  function formatMoney(cents, sample, moneyFormat) {
    if (cents == null || isNaN(cents)) return null;
    if (moneyFormat) {
      var shopFormatted = formatWithShopFormat(cents, moneyFormat);
      if (shopFormatted) return shopFormatted;
    }
    var rounded = Math.round(Number(cents) / 100);
    var thousands = ' ';
    var unit = 'kr';
    var unitLeading = false;
    if (sample && typeof sample === 'string') {
      var m = sample.match(/(-?[\d\.\s,]+)/);
      if (m) {
        var numeric = m[1];
        if (numeric.indexOf(',') > -1 && numeric.indexOf('.') === -1) thousands = '.';
        else if (numeric.indexOf('.') > -1 && numeric.indexOf(',') === -1) thousands = '.';
        var u = sample.replace(numeric, '').replace(/\s+/g, ' ').trim();
        if (u) { unit = u; unitLeading = sample.trim().indexOf(u) === 0; }
      }
    }
    var rendered = withThousands(String(rounded), thousands);
    if (!unit) return rendered;
    return unitLeading ? unit + ' ' + rendered : rendered + ' ' + unit;
  }

  // getResolution: the ONE source of truth. Prefer the resolution dentis-bundle-
  // config.liquid published; fall back to re-resolving through the SAME runtime
  // (never a forked resolver) so a late-loading config still repaints.
  function getResolution() {
    if (typeof window === 'undefined') return null;
    var published = window.EevyBundleResolved;
    if (published && published.effective) return published;
    var rt = window.Eevy && window.Eevy.bundles;
    var base = window.EevyBundleConfigBase || window.EevyBundleConfig;
    if (rt && base && typeof rt.resolveEffectiveBundleConfig === 'function') {
      try {
        var res = rt.resolveEffectiveBundleConfig(base);
        return {
          base: res.baseConfig,
          effective: res.effectiveBundleConfig,
          campaignKey: res.campaignKey,
          campaign: res.campaign
        };
      } catch (e) { DBG.warn && DBG.warn('cart-campaign: re-resolve failed', e); }
    }
    return null;
  }

  function tierOf(cfg, tierKey) {
    return (cfg && cfg.tiers && cfg.tiers[tierKey]) || null;
  }

  // componentsFor: the effective tier's components for the line's selection,
  // mirroring the Liquid fallback (selection -> default -> first).
  function componentsFor(cfg, tierKey, selection) {
    var tier = tierOf(cfg, tierKey);
    if (!tier || !tier.components_by_selection) return [];
    var by = tier.components_by_selection;
    return by[selection] || by['default'] || by[Object.keys(by)[0]] || [];
  }

  function bundleQtyFor(comp, totalQty) {
    var compQty = toInt(comp && comp.qty) || 1;
    var lineQty = toInt(totalQty) || compQty;
    var bundles = lineQty / compQty;
    return isFinite(bundles) && bundles > 0 ? bundles : 1;
  }

  function explicitFinalCents(comp, totalQty) {
    if (!comp || comp.final_price_cents == null) return null;
    var finalUnit = Number(comp.final_price_cents);
    if (!isFinite(finalUnit)) return null;
    return Math.round(finalUnit * bundleQtyFor(comp, totalQty));
  }

  // childFinalCents: prefer Eevy's exact per-component final_price_cents when
  // present. Fall back to the normal component role economics
  // (role=free -> 0; role=fixed -> unit*qty minus discount_amount_cents*qty
  // clamped to 0; discount_pct -> unit*qty*(100-pct)/100 floored; else unit*qty)
  // so non-enriched configs still render from their component object rather than
  // inventing prices from a tier total.
  function childFinalCents(cfg, comp, totalQty) {
    if (!comp) return null;
    var explicit = explicitFinalCents(comp, totalQty);
    if (explicit != null) return explicit;
    var items = (cfg && cfg.items) || {};
    var item = items[comp.item];
    if (!item) return null;
    var unit = toInt(item.unit_price_cents);
    if (comp.role === 'free') return 0;
    if (comp.role === 'fixed' && comp.discount_amount_cents != null) {
      var disc = (Number(comp.discount_amount_cents) || 0) * totalQty;
      return Math.max(0, unit * totalQty - disc);
    }
    if (comp.discount_pct != null) {
      var mult = 100 - Number(comp.discount_pct);
      return Math.floor((unit * totalQty * mult) / 100);
    }
    return unit * totalQty;
  }

  // childCompareCents: the strikethrough "before" value for a campaign child —
  // the component PRODUCT'S full ORIGINAL compare-at (e.g. heads 399 kr), NOT the
  // pre-campaign normal-bundle child final (baseFinal, e.g. the 35%-off 259 kr).
  // Emil's contract: a campaign changes the effective price, never the product's
  // original compare-at. Source precedence:
  //   1. the effective component's published per-unit compare_at_cents (when the
  //      Eevy resolver enriches components) × qty
  //   2. the Liquid-emitted original compare attribute (item compare-at × qty),
  //      a resolved-object-independent path
  //   3. baseFinal — only for older resolved objects carrying neither, preserving
  //      prior behavior in that edge case
  function childCompareCents(comp, childQty, node, baseFinal) {
    if (comp && comp.compare_at_cents != null) {
      var unit = Number(comp.compare_at_cents);
      if (isFinite(unit) && unit > 0) return Math.round(unit * (toInt(childQty) || 1));
    }
    var attr = node && node.getAttribute
      ? toInt(node.getAttribute('data-dentis-eevy-child-compare'))
      : 0;
    if (attr > 0) return attr;
    return baseFinal > 0 ? baseFinal : null;
  }

  function findComponent(comps, itemKey) {
    if (!comps || !itemKey) return null;
    for (var i = 0; i < comps.length; i++) {
      if (String(comps[i] && comps[i].item) === String(itemKey)) return comps[i];
    }
    return null;
  }

  // tierDisplayCents: the tier's per-bundle display price (the parent offer).
  function tierDisplayCents(cfg, tierKey) {
    var t = tierOf(cfg, tierKey);
    return (t && t.display_price_cents != null) ? Number(t.display_price_cents) : null;
  }

  // setCurrent: write the campaign offer into the line's current-price slot and
  // ensure a strikethrough showing the base "before" price. Idempotent: writing
  // the same formatted value twice is harmless; the strike node is created once.
  function setCurrent(priceEl, currentSel, strikeSel, strikeClass, finalCents, compareCents) {
    var current = priceEl.querySelector(currentSel);
    if (!current) return false;
    var sample = (current.textContent || '').trim();
    var moneyFormat = moneyFormatOf(priceEl);
    var formatted = formatMoney(finalCents, sample, moneyFormat);
    if (!formatted) return false;
    current.textContent = formatted;

    var strike = priceEl.querySelector(strikeSel);
    if (compareCents != null && compareCents > finalCents) {
      var strikeText = formatMoney(compareCents, sample, moneyFormat);
      if (strike) {
        strike.textContent = strikeText;
        if (strike.style) strike.style.display = '';
      } else if (strikeText) {
        strike = (typeof document !== 'undefined') ? document.createElement('s') : null;
        if (strike) {
          strike.className = strikeClass;
          strike.setAttribute('data-dentis-price-compare', '');
          strike.textContent = strikeText;
          if (current.parentNode) current.parentNode.insertBefore(strike, current);
        }
      }
    }
    return true;
  }

  function setChildFree(priceEl, currentSel, strikeSel, strikeClass, compareCents) {
    var current = priceEl.querySelector(currentSel);
    if (!current) return false;
    var sample = (current.textContent || '').trim();
    current.textContent = 'GRATIS';

    var strike = priceEl.querySelector(strikeSel);
    if (compareCents != null && compareCents > 0) {
      var strikeText = formatMoney(compareCents, sample, moneyFormatOf(priceEl));
      if (strike) {
        strike.textContent = strikeText || '';
        if (strike.style) strike.style.display = strikeText ? '' : 'none';
      } else if (strikeText) {
        strike = (typeof document !== 'undefined') ? document.createElement('s') : null;
        if (strike) {
          strike.className = strikeClass;
          strike.setAttribute('data-dentis-price-compare', '');
          strike.textContent = strikeText;
          if (current.parentNode) current.parentNode.insertBefore(strike, current);
        }
      }
    }
    return true;
  }

  // repaintParent: bring the bundle PARENT line to the effective campaign tier
  // offer. No-op when there's no active campaign, the tier is unknown, or the
  // effective offer equals the base offer (so non-campaign carts are untouched).
  function repaintParent(node, res) {
    var tierKey = node.getAttribute('data-dentis-eevy-tier');
    if (!tierKey || !res || !res.campaignKey) return;
    var qty = toInt(node.getAttribute('data-dentis-eevy-qty')) || 1;
    var effTier = tierOf(res.effective, tierKey);
    var baseTier = tierOf(res.base, tierKey);
    if (!effTier || effTier.display_price_cents == null) return;

    var effUnit = Number(effTier.display_price_cents);
    var baseUnit = (baseTier && baseTier.display_price_cents != null)
      ? Number(baseTier.display_price_cents)
      : toInt(node.getAttribute('data-dentis-eevy-base-price-cents'));
    if (!isFinite(effUnit) || effUnit === baseUnit) return;

    var effLine = effUnit * qty;
    var baseLine = baseUnit * qty;
    var liquidFinal = toInt(node.getAttribute('data-dentis-eevy-line-final'));

    // Surface a money gap: when the cart line price Shopify computed differs
    // from the live campaign offer, the campaign code may not be applied as
    // money. We still align the DISPLAY to the campaign offer (matches PDP +
    // picker) but log it loudly so a genuine application gap is visible.
    if (liquidFinal && liquidFinal !== effLine) {
      DBG.warn && DBG.warn('cart-campaign: parent line money/offer mismatch', {
        tier: tierKey, campaign: res.campaignKey, cartLineFinal: liquidFinal, campaignOfferLine: effLine
      });
    }

    // data-dentis-base-price (read by the order-scope painter) must reflect the
    // campaign offer so any order-level discount composes from the campaign
    // price, not the stale base.
    if (node.hasAttribute('data-dentis-base-price')) {
      node.setAttribute('data-dentis-base-price', String(effLine));
    }

    // The strike is the larger of the parent variant's compare-at × qty and
    // the line's original_line_price, emitted by Liquid (cart-drawer.liquid);
    // a variant without a compare-at leaves original_line_price. A campaign
    // changes the effective price, never this original. (The PDP strike
    // instead sums the Eevy config's item compare_at_cents.) baseLine (the
    // normal bundle price) only for older markup without the attribute.
    var originalCompare = toInt(node.getAttribute('data-dentis-eevy-compare-cents'));
    setCurrent(
      node,
      '.dentis-cart-card__final-price',
      '.dentis-cart-card__old-price',
      'dentis-cart-card__old-price',
      effLine,
      originalCompare > 0 ? originalCompare : baseLine
    );
    node.setAttribute('data-dentis-cart-campaign-applied', String(effLine));
  }

  // repaintChild: bring a config-driven child line to the exact effective
  // component economics Eevy resolved for the shopper's active campaign. The
  // durable contract is: campaign resolution publishes a complete effective
  // bundle object, and the drawer consumes component finals directly. It should
  // never proportionally split a parent tier total across children.
  function repaintChild(node, res) {
    var tierKey = node.getAttribute('data-dentis-eevy-child-tier');
    var itemKey = node.getAttribute('data-dentis-eevy-child-item');
    if (!tierKey || !itemKey || !res || !res.campaignKey) return;
    var selection = node.getAttribute('data-dentis-eevy-child-selection') || '';
    var childQty = toInt(node.getAttribute('data-dentis-eevy-child-qty')) || 1;
    var baseFinal = toInt(node.getAttribute('data-dentis-eevy-child-base-final'));

    var effComp = findComponent(componentsFor(res.effective, tierKey, selection), itemKey);
    if (!effComp) return;

    var effFinal = childFinalCents(res.effective, effComp, childQty);
    if (effFinal == null || effFinal === baseFinal) return;
    // Protect already-free base lines from being flipped to a price by a display
    // repaint. Campaign-free conversion of a paid base line is allowed below.
    if (baseFinal <= 0 && effFinal > 0) return;

    // data-dentis-base-price (read by the order-scope painter) must reflect the
    // campaign child price so an active order-level discount (e.g. an influencer
    // "20% off order" code) composes from the campaign economics, not the stale
    // base — mirroring repaintParent.
    if (node.hasAttribute('data-dentis-base-price')) {
      node.setAttribute('data-dentis-base-price', String(effFinal));
    }

    // The strikethrough compare is the product's full original compare-at, NOT
    // baseFinal (the pre-campaign normal-bundle child final). setChildFree /
    // setCurrent keep the "only strike when compare > final" guards, so a child
    // charged at full original shows no strike.
    var compareCents = childCompareCents(effComp, childQty, node, baseFinal);

    if (effFinal <= 0 || effComp.role === 'free') {
      setChildFree(
        node,
        '.dentis-included-card__free-price',
        '.dentis-included-card__compare-price',
        'dentis-included-card__compare-price',
        compareCents
      );
      node.setAttribute('data-dentis-cart-campaign-applied', String(effFinal));
      return;
    }

    setCurrent(
      node,
      '.dentis-included-card__free-price',
      '.dentis-included-card__compare-price',
      'dentis-included-card__compare-price',
      effFinal,
      compareCents
    );
    node.setAttribute('data-dentis-cart-campaign-applied', String(effFinal));
  }

  function repaintAll() {
    if (typeof document === 'undefined') return;
    var res = getResolution();
    if (!res || !res.campaignKey || !res.effective) return;
    var parents = document.querySelectorAll('[data-dentis-eevy-tier]');
    for (var p = 0; p < parents.length; p++) {
      try { repaintParent(parents[p], res); } catch (e) { DBG.warn && DBG.warn('repaintParent threw', e); }
    }
    var children = document.querySelectorAll('[data-dentis-eevy-child-item]');
    for (var c = 0; c < children.length; c++) {
      try { repaintChild(children[c], res); } catch (e) { DBG.warn && DBG.warn('repaintChild threw', e); }
    }
  }

  if (typeof document !== 'undefined') {
    // Campaign resolved/changed by the sovereign runtime.
    document.addEventListener('eevy:bundle-config-resolved', function () { repaintAll(); });
    // Cart-sync replaced the drawer innerHTML with fresh BASE Liquid -> re-derive
    // + re-apply so the campaign economics persist across the re-render cycle.
    document.addEventListener('dentis:cart-drawer:updated', function () { repaintAll(); });

    function boot() {
      // The resolution may have published before this script attached (the
      // config snippet runs inline at the top of the drawer). Apply now, then
      // once more on the next tick to catch an async runtime install.
      repaintAll();
      setTimeout(repaintAll, 0);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }

  // CommonJS export for the Node test harness. No-op in browsers.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      formatMoney: formatMoney,
      formatWithShopFormat: formatWithShopFormat,
      getResolution: getResolution,
      componentsFor: componentsFor,
      childFinalCents: childFinalCents,
      childCompareCents: childCompareCents,
      tierDisplayCents: tierDisplayCents,
      repaintParent: repaintParent,
      repaintChild: repaintChild,
      repaintAll: repaintAll
    };
  }
})();
